import React from 'react';
import { z } from 'zod';
import { safeUrl } from '../url';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Feature list — Strapi sections.about-new-feature + components.about-feature.
// Alternating image/text rows (title + description + optional link). Used by
// about, partner, life-at-cymmetri. No styled-system / next.
export const featureListSchema = z.object({
  items: z
    .array(
      z.object({
        title: z.string().min(1),
        description: z.string().optional(),
        imageUrl: z.string().optional(),
        linkText: z.string().optional(),
        linkUrl: safeUrl.optional(),
      }),
    )
    .min(1),
});

type FeatureListProps = z.infer<typeof featureListSchema>;

const Section = styled.section`
  max-width: 120rem;
  margin: 0 auto;
  padding: 6rem 1.6rem;
  display: grid;
  gap: 5rem;

  @media (min-width: 64em) {
    padding: 8rem 2.4rem;
    gap: 8rem;
  }
`;

const Row = styled.div<{ $reverse: boolean }>`
  display: grid;
  gap: 2.4rem;
  align-items: center;

  @media (min-width: 64em) {
    grid-template-columns: 1fr 1fr;
    gap: 5rem;

    & > figure {
      order: ${({ $reverse }) => ($reverse ? 2 : 1)};
    }
    & > div {
      order: ${({ $reverse }) => ($reverse ? 1 : 2)};
    }
  }
`;

const Media = styled.figure`
  margin: 0;
`;

const Image = styled.img`
  width: 100%;
  border-radius: 2rem;
  object-fit: cover;
  background: #f4f7ff;
`;

const Title = styled.h2`
  margin: 0;
  color: #0b1020;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: clamp(2.4rem, 3.5vw, 3.6rem);
`;

const Description = styled.p`
  margin: 1.2rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  line-height: 1.7;
`;

const Link = styled.a`
  display: inline-block;
  margin-top: 1.6rem;
  color: #063091;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 1.5rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

function FeatureList({ items }: FeatureListProps) {
  return (
    <Section>
      {items.map((item, i) => (
        <Row key={`${item.title}-${i}`} $reverse={i % 2 === 1}>
          <Media>
            {item.imageUrl ? <Image src={item.imageUrl} alt={item.title} loading="lazy" /> : null}
          </Media>
          <div>
            <Title>{item.title}</Title>
            {item.description ? <Description>{item.description}</Description> : null}
            {item.linkText && item.linkUrl ? <Link href={item.linkUrl}>{item.linkText} →</Link> : null}
          </div>
        </Row>
      ))}
    </Section>
  );
}

export const featureListBlock: BlockDefinition<FeatureListProps> = {
  type: 'featureList',
  schema: featureListSchema,
  Renderer: FeatureList,
};
